import * as React from 'react';

import type { ImageType } from '@coursera-web/cds-labs';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import CardTakeover from 'bundles/campaigns/components/card-takeover/CardTakeover';
import type { LinkButton, MobileBreakpoint } from 'bundles/campaigns/components/card-takeover/CardTakeover';
import PrePurchaseUpsellCourseraPlusModal from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus-common/hooks/useCourseraPlusSubscriptionFreeTrials';

import CplusLogo from 'images/unified-common/coursera-plus-logo-invert.svg';
import EOYDesktopCardImage from 'images/unified-common/cplus-upsell-eoy-desktop-card-image.png';
import EOYMobileCardImage from 'images/unified-common/cplus-upsell-eoy-mobile-image.png';
import NYDesktopCardImage from 'images/unified-common/cplus-upsell-ny-desktop-card-image.png';
import NYMobileCardImage from 'images/unified-common/cplus-upsell-ny-mobile-image.png';

import _t from 'i18n!nls/unified-common';

type Props = {
  mobileBreakpoint?: MobileBreakpoint;
  eventingData?: {
    modulePlacement?: 'HIGH' | 'LOW';
    index?: number;
  };
  forceOpenInNewTab?: boolean;
};

/**
 *
 * @property mobileBreakpoint: Starting at this breakpoint and at all lower breakpoints, the mobile design will be displayed
 * @returns
 */
const CourseraPlusUpsellCard = ({ mobileBreakpoint = 'sm', eventingData, forceOpenInNewTab }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down(mobileBreakpoint));

  const newYearPromoStartDate = new Date(2025, 11, 29);
  const isNewYearPromoActive = new Date() >= newYearPromoStartDate;

  const cardTitle = isNewYearPromoActive
    ? _t('Next level skills. New Year savings.')
    : _t('Unlimited growth. Unbeatable savings.');

  const DesktopCardImage = isNewYearPromoActive ? NYDesktopCardImage : EOYDesktopCardImage;
  const MobileCardImage = isNewYearPromoActive ? NYMobileCardImage : EOYMobileCardImage;

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { userHasCourseraPlusFreeTrial: userIsEligibleForFreeTrial } = useCourseraPlusSubscriptionFreeTrials();

  const cta: LinkButton = {
    text: _t('Save on Coursera Plus'),
    buttonName: 'c_plus_learn_more',
    href: '/courseraplus/special/new-year-2026-global',
    ariaLabel: _t('Save on Coursera Plus'),
  };

  const cplusLogoEyebrow: ImageType = {
    url: CplusLogo,
    height: isMobile ? 12 : 18,
    useHeightPixelValue: true,
  };

  const image: ImageType = {
    url: isMobile ? MobileCardImage : DesktopCardImage,
  };

  return (
    <React.Fragment>
      <PrePurchaseUpsellCourseraPlusModal
        handleClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        hasCourseraPlusFreeTrial={userIsEligibleForFreeTrial}
      />
      <CardTakeover
        title={cardTitle}
        cta={cta}
        eyebrowImage={cplusLogoEyebrow}
        image={image}
        imageDisplayConfig={{ displayUnderButton: true }}
        mobileBreakpoint={mobileBreakpoint}
        forceOpenInNewTab={forceOpenInNewTab}
        eventingData={{
          merchModule: {
            moduleType: 'coursera_plus_upsell_card',
            heading: cardTitle,
            hasImage: true,
            index: eventingData?.index,
          },
          buttonName: 'c_plus_learn_more',
        }}
        dataTestId="coursera-plus-upsell-card"
      />
    </React.Fragment>
  );
};

export default CourseraPlusUpsellCard;
